/* C4 design carousel for Print.App -- product.c4, online state only.
   ------------------------------------------------------------------------------------------
   Successor to the PitchPrint rail in c4-pitchprint.js (same markup, same c4-pdp.css classes, same
   arrows, loop and "Start again" confirm), rebuilt on Print.App's own data.

   WHERE THE DESIGNS COME FROM. Print.App's client puts the product's mapped designs in the page:
   window.printAppInstance.model.env.designList = [{ id, title, thumbnails:[front, back] }]. No
   pp-designs.json, no export step: map a design to the product in Print.App admin and it is here.

   CATEGORIES. The storefront list carries no tags, so the design TITLE carries them (decision,
   24 Sep 2026): "Organic | cafe, florist" -- display name left of the pipe, comma-separated tags
   right. Chips are built from the tags; a title without a pipe is untagged and shows under All.

   OPENING A CHOSEN DESIGN (verified live 24 Sep 2026 on Loyalty Cards - test). The editor reads
   env.designId ONCE, when its iframe announces itself and the client answers with model.env; the
   designId inside a later app:show is ignored. So: set env.designId, replace the iframe so the
   editor announces again, wait for that announce, then showApp. Every step is guarded and falls
   back to a plain showApp() (the editor's own template picker) if the client's internals change.

   A SAVED DESIGN WINS. When the page already holds a project (#_printapp), the editor resumes it
   whatever design we name -- same as PitchPrint. Switching template therefore means clearing the
   project first; we ask ("Start again" / "Keep my design"), park the chosen id, let Print.App's
   clearDesign() do its storage-clear-and-reload, and open the parked id on the next load.

   SEE ALL DESIGNS (25 Sep 2026). Once there is a rail's worth, the last tile in the rail opens a
   full-screen overlay: every mapped design in a grid, narrowed by the same industry tags, by stamp
   count ("6 stamps" / "10 stamps" read from the name) and by a search box. Customise on a grid tile
   goes through the same chooseTemplate path as the rail, so the saved-design popup still applies.
   ------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  var card = document.querySelector('[data-c4-design-card]');
  if (!card) return;
  var host = card.parentNode;
  if (!host) return;
  var radio = card.querySelector('input[type="radio"]');

  var PENDING = 'c4pa-pending-design';
  var all = [];            // parsed designs
  var tag = '';            // '' = All
  var carousel, rail, countEl, catsEl, emptyEl, moreEl;
  var overlay = null, oBox, oGrid, oCats, oStamps, oSearch, oCount, oEmpty, oFilterBtn;   // "See all" overlay
  var oTag = '', oStamp = '', oQuery = '';
  var RAIL_TAGS = 8;       // chips on the rail before "More" (all tags stay in the overlay)
  var SEE_ALL_FROM = 5;    // show the "See all" tile once the rail holds this many designs
  var bootedId = null;     // design id the editor frame was last booted with (null = untouched)

  /* ------------------------------------------------------------------ Print.App access ---- */
  function client() {
    var c = window.printAppInstance;
    return c && typeof c.showApp === 'function' ? c : null;
  }
  function rawList() {
    var c = client();
    var l = c && c.model && c.model.env && c.model.env.designList;
    return Array.isArray(l) ? l : [];
  }
  function parse(d) {
    var t = String(d.title || '');
    var i = t.indexOf('|');
    var name = (i >= 0 ? t.slice(0, i) : t).trim();
    var tags = i >= 0 ? t.slice(i + 1).split(',') : [];
    var out = [];
    for (var k = 0; k < tags.length; k++) {
      var s = tags[k].trim().toLowerCase();
      if (s && out.indexOf(s) === -1) out.push(s);
    }
    var thumbs = [];
    var th = d.thumbnails || [];
    for (var j = 0; j < th.length; j++) if (/^https?:\/\//i.test(th[j])) thumbs.push(th[j]);
    var sm = /(\d+)\s*stamps?\b/i.exec(name);
    return { id: d.id, name: name || 'Design', tags: out, thumbs: thumbs, stamps: sm ? sm[1] : '' };
  }
  function loadDesigns() {
    var raw = rawList(), out = [];
    for (var i = 0; i < raw.length; i++) {
      if (!raw[i] || !raw[i].id) continue;
      out.push(parse(raw[i]));
    }
    return out;
  }

  /* The saved project id lives in the Print.App inputs (#_printapp); same read as c4-print-app.js. */
  function projectId() {
    var ins = document.querySelectorAll('input, textarea');
    for (var i = 0; i < ins.length; i++) {
      var n = (ins[i].name || '').toLowerCase();
      if (!/printapp/.test(n) || n.indexOf('pdf') !== -1) continue;
      var v = (ins[i].value || '').trim();
      if (v && v.indexOf('http') !== 0) return v;
    }
    return null;
  }
  function sourceDesignId() {
    try {
      var c = client();
      var pid = c && c.model && c.model.env && c.model.env.product && (c.model.env.product.id || c.model.env.product);
      var sp = JSON.parse(window.localStorage.getItem('print-app-sp') || '{}');
      var entry = pid && sp[String(pid)];
      return entry && entry.sourceDesignId ? String(entry.sourceDesignId) : null;
    } catch (e) { return null; }
  }

  /* ------------------------------------------------------------------ open a design ------- */
  function working(title, body) {
    try { document.dispatchEvent(new CustomEvent('c4:working', { detail: { title: title, body: body } })); } catch (e) {}
  }
  function done() {
    try { document.dispatchEvent(new CustomEvent('c4:done')); } catch (e) {}
  }
  function chooseDesignRoute() {
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    card.setAttribute('data-c4-touched', '');
  }

  /* Boot (or re-boot) the editor frame on `id`, then show it. */
  function openDesign(id) {
    var c = client();
    if (!c) return false;
    chooseDesignRoute();
    var env = c.model && c.model.env, ui = c.model && c.model.ui, st = c.model && c.model.state;
    var canRebuild = env && ui && st && typeof c.makeFrame === 'function' && typeof c.handleDisplayMode === 'function';
    if (!canRebuild) { c.showApp(); return true; }

    working('Opening the design editor', 'Loading your template. This only takes a moment.');
    env.designId = id;
    if (!projectId()) env.mode = 'new-project';

    var announced = !!ui.messageSource;
    if (!announced) {
      /* Frame still booting: the env it is about to receive already carries designId. */
      bootedId = id;
      finish();
      return true;
    }
    if (bootedId === id) { finish(); return true; }

    /* Re-boot: a fresh iframe announces, the client answers with env (now carrying designId). */
    try {
      if (st.shown && typeof c.closeApp === 'function') c.closeApp();
      ui.messageSource = null;
      if (ui.frame && ui.frame.remove) ui.frame.remove();
      ui.frame = c.makeFrame();
      st.shown = false;
      c.handleDisplayMode();
      if (!document.contains(ui.frame)) document.body.appendChild(ui.frame);
    } catch (e) {
      done();
      c.showApp();
      return true;
    }
    bootedId = id;
    var tries = 0;
    (function waitAnnounce() {
      if (ui.messageSource) { finish(); return; }
      if (tries++ > 60) { finish(); return; }          // ~9s: show anyway; app:show is queued
      setTimeout(waitAnnounce, 150);
    })();
    return true;

    function finish() {
      try { c.showApp({ designId: id }); } catch (e) { try { c.showApp(); } catch (e2) {} }
      setTimeout(done, 400);
    }
  }

  /* Picking a different template while a design exists: ask, clear, reload, resume. */
  function chooseTemplate(id, name) {
    if (!projectId()) { openDesign(id); return; }
    askBeforeSwitch(id, name);
  }
  /* A PROPER POPUP, not a line under the rail (Cathy, 24 Sep: with Customise still looking live "it
     looks like its not working"). Three ways out: start again on the chosen template, open the
     current design so they can Save for later (Print.App's own button, which asks them to sign in),
     or keep it. Scrim + dialog, Escape/scrim close, focus returns to the rail. */
  function askBeforeSwitch(id, name) {
    var old = document.querySelector('[data-c4-ppc-modal]');
    if (old) old.remove();
    var scrim = document.createElement('div');
    scrim.className = 'c4-ppcm';
    scrim.setAttribute('data-c4-ppc-modal', '');
    var box = document.createElement('div');
    box.className = 'c4-ppcm__box';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'c4-ppcm-title');

    var h = document.createElement('h3');
    h.id = 'c4-ppcm-title';
    h.className = 'c4-ppcm__title';
    h.textContent = 'You already have a design on this product';
    var p = document.createElement('p');
    p.className = 'c4-ppcm__body';
    p.textContent = 'Starting again from "' + (name || 'this template') + '" will replace it. If you want to come back to your current design later, save it first (you will be asked to sign in).';

    var go = button('Start again with "' + (name || 'this template') + '"', 'c4-ppcm__btn c4-ppcm__btn--go', function () { close(); startOver(id); });
    var save = button('Save my design first', 'c4-ppcm__btn', function () {
      close();
      var c = client();
      if (c) { try { c.showApp(); } catch (e) {} }
    });
    var keep = button('Keep my design', 'c4-ppcm__btn c4-ppcm__btn--quiet', close);

    var row = document.createElement('div');
    row.className = 'c4-ppcm__row';
    row.appendChild(go); row.appendChild(save); row.appendChild(keep);
    box.appendChild(h); box.appendChild(p); box.appendChild(row);
    scrim.appendChild(box);
    document.body.appendChild(scrim);
    scrim.addEventListener('click', function (e) { if (e.target === scrim) close(); });
    document.addEventListener('keydown', onKey);
    go.focus();

    function onKey(e) { if (e.key === 'Escape') close(); }
    function close() {
      document.removeEventListener('keydown', onKey);
      scrim.remove();
      try { rail.focus({ preventScroll: true }); } catch (e) {}
    }
    function button(label, cls, fn) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.textContent = label;
      b.addEventListener('click', fn);
      return b;
    }
  }
  function startOver(id) {
    var c = client();
    try { sessionStorage.setItem(PENDING, id); } catch (e) {}
    working('Clearing your design', 'Starting again from the template you chose.');
    var cleared = false;
    if (c && typeof c.clearDesign === 'function') {
      try { c.clearDesign(); cleared = true; } catch (e) {}
    }
    if (!cleared) {
      var rm = card.querySelector('[data-c4-design-remove]');
      if (rm && !rm.hidden) { rm.click(); cleared = true; }
    }
    /* Print.App's loader clears its storage and reloads the page (~3s). If no reload comes, open
       here rather than leave the customer on a working dialog. */
    setTimeout(function () {
      var pending = null;
      try { pending = sessionStorage.getItem(PENDING); } catch (e) {}
      if (!pending) return;                        // the reload consumed it
      try { sessionStorage.removeItem(PENDING); } catch (e) {}
      done();
      openDesign(id);
    }, 6000);
  }
  function resumePending() {
    var id = null;
    try { id = sessionStorage.getItem(PENDING); sessionStorage.removeItem(PENDING); } catch (e) {}
    if (!id || projectId()) return;
    var known = false;
    for (var i = 0; i < all.length; i++) if (all[i].id === id) known = true;
    if (!known) return;
    select(id);
    openDesign(id);
  }

  /* ------------------------------------------------------------------ rail ---------------- */
  function buildCarousel() {
    if (carousel) return;
    carousel = document.createElement('div');
    carousel.className = 'c4-ppc';
    carousel.setAttribute('data-c4-design-carousel', '');
    carousel.hidden = true;

    var head = document.createElement('div');
    head.className = 'c4-ppc__hd';
    var h = document.createElement('span');
    h.className = 'c4-ppc__title';
    h.textContent = 'Choose a free template';
    countEl = document.createElement('span');
    countEl.className = 'c4-ppc__count';
    head.appendChild(h); head.appendChild(countEl);

    catsEl = document.createElement('div');
    catsEl.className = 'c4-ppc__cats';
    catsEl.setAttribute('role', 'tablist');
    catsEl.setAttribute('aria-label', 'Template categories');
    catsEl.hidden = true;
    catsEl.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-c4-tag]') : null;
      if (!b) return;
      e.preventDefault();
      tag = b.getAttribute('data-c4-tag') || '';
      paintCats();
      paintCarousel();
    });

    var body = document.createElement('div');
    body.className = 'c4-ppc__body';
    rail = document.createElement('div');
    rail.className = 'c4-ppc__rail';
    rail.setAttribute('role', 'list');

    /* Delegated: the loop appends clones, and cloneNode copies no listeners. */
    rail.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.c4-ppc__all')) {
        e.preventDefault();
        e.stopPropagation();
        openOverlay();
        return;
      }
      var item = t.closest('.c4-ppc__item');
      if (!item || !rail.contains(item)) return;
      var id = item.getAttribute('data-design-id');
      if (t.closest('.c4-ppc__go')) {
        e.preventDefault();
        e.stopPropagation();
        select(id);
        chooseTemplate(id, item.getAttribute('data-design-name') || '');
        return;
      }
      select(id);
    });
    /* Front on rest, back on hover -- both sides are in the list, so show them. */
    rail.addEventListener('mouseover', function (e) { flip(e, true); });
    rail.addEventListener('mouseout', function (e) { flip(e, false); });

    var settle;
    rail.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () {
        var loop = loopWidth();
        if (loop && rail.scrollLeft >= loop) rail.scrollLeft = rail.scrollLeft - loop;
      }, 140);
    });

    body.appendChild(arrow('prev', '‹'));
    body.appendChild(rail);
    body.appendChild(arrow('next', '›'));

    emptyEl = document.createElement('p');
    emptyEl.className = 'c4-ppc__empty';
    emptyEl.hidden = true;
    emptyEl.textContent = 'No templates in this category yet. Customise starts you from a blank one.';

    /* Line under the rail: "See all N designs" -> the overlay (Cathy, 25 Sep: wanted as well as the tile). */
    moreEl = document.createElement('p');
    moreEl.className = 'c4-ppc__more';
    moreEl.hidden = true;
    var moreBtn = document.createElement('button');
    moreBtn.type = 'button';
    moreBtn.className = 'c4-ppc__morelink';
    moreBtn.setAttribute('aria-haspopup', 'dialog');
    moreBtn.addEventListener('click', function (e) { e.preventDefault(); openOverlay(); });
    moreEl.appendChild(moreBtn);

    carousel.appendChild(head);
    carousel.appendChild(catsEl);
    carousel.appendChild(body);
    carousel.appendChild(moreEl);
    carousel.appendChild(emptyEl);
    host.appendChild(carousel);
  }

  function flip(e, over) {
    var t = e.target;
    if (!t || !t.closest) return;
    var item = t.closest('[data-design-id]');
    if (!item) return;
    var img = item.querySelector('img');
    var back = item.getAttribute('data-back');
    var front = item.getAttribute('data-front');
    if (!img || !back || !front) return;
    var want = over ? back : front;
    if (img.getAttribute('src') !== want) img.setAttribute('src', want);
  }

  function paintCats() {
    if (!catsEl) return;
    var seen = {}, list = [];
    for (var i = 0; i < all.length; i++) {
      for (var k = 0; k < all[i].tags.length; k++) {
        var t = all[i].tags[k];
        if (seen[t]) { seen[t]++; continue; }
        seen[t] = 1; list.push(t);
      }
    }
    if (!list.length) { catsEl.hidden = true; catsEl.textContent = ''; tag = ''; return; }
    list.sort(function (a, b) { return (seen[b] - seen[a]) || a.localeCompare(b); });
    if (tag && !seen[tag]) tag = '';
    catsEl.hidden = false;
    catsEl.textContent = '';
    chip('', 'All');
    /* TOP TAGS ONLY (Cathy, 26 Oct 2026: on Same Day the rail showed 160+ chips, ~6000px of buttons,
       because the new designs carry many tags each). The rail keeps the most-used RAIL_TAGS tags; the
       chosen tag always stays visible; "More" opens the See-all overlay, which has every tag. */
    var shown = list.slice(0, RAIL_TAGS);
    if (tag && shown.indexOf(tag) === -1) shown.push(tag);
    for (var j = 0; j < shown.length; j++) chip(shown[j], shown[j].charAt(0).toUpperCase() + shown[j].slice(1));
    if (list.length > shown.length) {
      var mb = document.createElement('button');
      mb.type = 'button';
      mb.className = 'c4-ppc__cat c4-ppc__cat--more';
      mb.setAttribute('aria-haspopup', 'dialog');
      mb.textContent = 'More (' + (list.length - shown.length) + ') \u2192';
      mb.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); openOverlay(); });
      catsEl.appendChild(mb);
    }
    function chip(value, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'c4-ppc__cat' + (value === tag ? ' is-on' : '');
      b.setAttribute('data-c4-tag', value);
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', value === tag ? 'true' : 'false');
      b.textContent = label;
      catsEl.appendChild(b);
    }
  }

  function arrow(dir, glyph) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'c4-ppc__arrow c4-ppc__arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? 'Previous templates' : 'More templates');
    b.textContent = glyph;
    b.addEventListener('click', function (e) {
      e.preventDefault();
      if (!rail) return;
      var step = Math.max(rail.clientWidth - 40, 160);
      var loop = loopWidth();
      if (!loop) {
        var max = Math.max(rail.scrollWidth - rail.clientWidth, 0);
        rail.scrollTo({
          left: dir === 'prev' ? Math.max(rail.scrollLeft - step, 0) : Math.min(rail.scrollLeft + step, max),
          behavior: 'smooth'
        });
        return;
      }
      if (dir === 'prev' && rail.scrollLeft - step < 0) rail.scrollLeft = rail.scrollLeft + loop;
      rail.scrollTo({ left: rail.scrollLeft + (dir === 'prev' ? -step : step), behavior: 'smooth' });
    });
    return b;
  }

  function loopWidth() {
    if (!rail) return 0;
    var firstClone = rail.querySelector('.c4-ppc__item.is-clone');
    return firstClone ? firstClone.offsetLeft : 0;
  }

  function visible() {
    if (!tag) return all.slice();
    var out = [];
    for (var i = 0; i < all.length; i++) if (all[i].tags.indexOf(tag) !== -1) out.push(all[i]);
    return out;
  }

  function paintCarousel() {
    if (!carousel || !rail) return;
    var designs = visible();
    var current = sourceDesignId();
    rail.textContent = '';
    if (countEl) countEl.textContent = designs.length ? designs.length + (designs.length === 1 ? ' design' : ' designs') : '';
    if (emptyEl) emptyEl.hidden = !!designs.length;

    for (var i = 0; i < designs.length; i++) {
      var d = designs[i];
      var item = document.createElement('div');
      item.className = 'c4-ppc__item' + (current && current === d.id ? ' is-selected' : '');
      item.setAttribute('role', 'listitem');
      item.setAttribute('data-design-id', d.id);
      item.setAttribute('data-design-name', d.name);
      if (d.thumbs[0]) item.setAttribute('data-front', d.thumbs[0]);
      if (d.thumbs[1]) item.setAttribute('data-back', d.thumbs[1]);

      var wrap = document.createElement('div');
      wrap.className = 'c4-ppc__imgwrap';
      var img = document.createElement('img');
      img.className = 'c4-ppc__img';
      if (d.thumbs[0]) img.src = d.thumbs[0];
      img.alt = d.name;
      img.loading = 'lazy';
      img.decoding = 'async';
      (function (id) {
        img.addEventListener('error', function () {
          var same = rail.querySelectorAll('.c4-ppc__item');
          for (var k = same.length - 1; k >= 0; k--) if (same[k].getAttribute('data-design-id') === id) same[k].remove();
        });
      })(d.id);

      var go = document.createElement('button');
      go.type = 'button';
      go.className = 'c4-ppc__go';
      go.textContent = 'Customise';

      wrap.appendChild(img); wrap.appendChild(go);
      var name = document.createElement('span');
      name.className = 'c4-ppc__name';
      name.textContent = d.name;
      item.appendChild(wrap); item.appendChild(name);
      rail.appendChild(item);
    }

    /* Last tile: "See all N designs" -> the overlay. Counts every mapped design, not the chip's subset. */
    if (all.length >= SEE_ALL_FROM) {
      var more = document.createElement('div');
      more.className = 'c4-ppc__item c4-ppc__item--all';
      more.setAttribute('role', 'listitem');
      var mwrap = document.createElement('div');
      mwrap.className = 'c4-ppc__imgwrap';
      var mb = document.createElement('button');
      mb.type = 'button';
      mb.className = 'c4-ppc__all';
      mb.setAttribute('aria-haspopup', 'dialog');
      var mn = document.createElement('span');
      mn.className = 'c4-ppc__alln';
      mn.textContent = String(all.length);
      var mt = document.createElement('span');
      mt.className = 'c4-ppc__allt';
      mt.textContent = 'See all designs';
      mb.appendChild(mn); mb.appendChild(mt);
      mwrap.appendChild(mb);
      var mname = document.createElement('span');
      mname.className = 'c4-ppc__name';
      mname.textContent = 'Browse and filter';
      more.appendChild(mwrap); more.appendChild(mname);
      rail.appendChild(more);
    }

    if (moreEl) {
      moreEl.hidden = all.length < SEE_ALL_FROM;
      var ml = moreEl.querySelector('.c4-ppc__morelink');
      if (ml) ml.textContent = 'See all ' + all.length + ' designs →';
    }

    if (rail.scrollWidth > rail.clientWidth + 4) {
      var reals = rail.querySelectorAll('.c4-ppc__item');
      for (var j = 0; j < reals.length; j++) {
        var clone = reals[j].cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        rail.appendChild(clone);
      }
    }
    rail.scrollLeft = 0;
    syncVisibility();
  }

  function select(id) {
    if (!rail) return;
    var kids = rail.querySelectorAll('.c4-ppc__item');
    for (var i = 0; i < kids.length; i++) kids[i].classList.toggle('is-selected', kids[i].getAttribute('data-design-id') === id);
    chooseDesignRoute();
  }

  /* Shown on the design route whenever there are designs. Stays up after a save so the customer
     can switch template (chooseTemplate asks first). */
  function syncVisibility() {
    if (!carousel) return;
    var wanted = !!all.length && !!(radio && radio.checked);
    if (carousel.hidden === wanted) carousel.hidden = !wanted;
  }

  /* ------------------------------------------------------------------ see-all overlay ----- */
  function stampValues() {
    var seen = {}, list = [];
    for (var i = 0; i < all.length; i++) {
      var s = all[i].stamps;
      if (!s || seen[s]) continue;
      seen[s] = 1; list.push(s);
    }
    list.sort(function (a, b) { return Number(a) - Number(b); });
    return list;
  }
  function overlayVisible() {
    var q = oQuery.trim().toLowerCase(), out = [];
    for (var i = 0; i < all.length; i++) {
      var d = all[i];
      if (oTag && d.tags.indexOf(oTag) === -1) continue;
      if (oStamp && d.stamps !== oStamp) continue;
      if (q) {
        var hay = (d.name + ' ' + d.tags.join(' ')).toLowerCase();
        if (hay.indexOf(q) === -1) continue;
      }
      out.push(d);
    }
    return out;
  }
  function buildOverlay() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'c4-ppco';
    overlay.setAttribute('data-c4-ppc-overlay', '');
    overlay.hidden = true;
    var box = oBox = document.createElement('div');
    box.className = 'c4-ppco__box';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'c4-ppco-title');

    var head = document.createElement('div');
    head.className = 'c4-ppco__hd';
    var h = document.createElement('h3');
    h.id = 'c4-ppco-title';
    h.className = 'c4-ppco__title';
    h.textContent = 'All templates';
    oCount = document.createElement('span');
    oCount.className = 'c4-ppco__count';
    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'c4-ppco__close';
    x.setAttribute('aria-label', 'Close');
    x.innerHTML = '&times;';
    x.addEventListener('click', closeOverlay);
    head.appendChild(h); head.appendChild(oCount); head.appendChild(x);

    var tools = document.createElement('div');
    tools.className = 'c4-ppco__tools';
    oSearch = document.createElement('input');
    oSearch.type = 'search';
    oSearch.className = 'c4-ppco__search';
    oSearch.placeholder = 'Search templates';
    oSearch.setAttribute('aria-label', 'Search templates');
    oSearch.autocomplete = 'off';
    oSearch.addEventListener('input', function () { oQuery = oSearch.value || ''; paintOverlay(); });
    tools.appendChild(oSearch);
    /* Phones only (CSS): the chip rows hide behind this button so the grid gets the screen. */
    oFilterBtn = document.createElement('button');
    oFilterBtn.type = 'button';
    oFilterBtn.className = 'c4-ppco__ftoggle';
    oFilterBtn.setAttribute('aria-expanded', 'false');
    oFilterBtn.hidden = true;
    oFilterBtn.addEventListener('click', function () {
      var open = !box.classList.contains('is-filters-open');
      box.classList.toggle('is-filters-open', open);
      oFilterBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    tools.appendChild(oFilterBtn);

    oCats = document.createElement('div');
    oCats.className = 'c4-ppc__cats c4-ppco__cats';
    oCats.setAttribute('role', 'tablist');
    oCats.setAttribute('aria-label', 'Industry');
    oCats.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-c4-otag]') : null;
      if (!b) return;
      e.preventDefault();
      oTag = b.getAttribute('data-c4-otag') || '';
      paintOverlayChips();
      paintOverlay();
    });
    oStamps = document.createElement('div');
    oStamps.className = 'c4-ppc__cats c4-ppco__cats c4-ppco__stamps';
    oStamps.setAttribute('role', 'tablist');
    oStamps.setAttribute('aria-label', 'Number of stamps');
    oStamps.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-c4-ostamp]') : null;
      if (!b) return;
      e.preventDefault();
      oStamp = b.getAttribute('data-c4-ostamp') || '';
      paintOverlayChips();
      paintOverlay();
    });

    oGrid = document.createElement('div');
    oGrid.className = 'c4-ppco__grid';
    oGrid.setAttribute('role', 'list');
    oGrid.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var item = t.closest('.c4-ppco__item');
      if (!item) return;
      e.preventDefault();
      var id = item.getAttribute('data-design-id');
      var name = item.getAttribute('data-design-name') || '';
      closeOverlay();
      select(id);
      chooseTemplate(id, name);
    });
    oGrid.addEventListener('mouseover', function (e) { flip(e, true); });
    oGrid.addEventListener('mouseout', function (e) { flip(e, false); });

    oEmpty = document.createElement('p');
    oEmpty.className = 'c4-ppc__empty c4-ppco__empty';
    oEmpty.hidden = true;
    oEmpty.textContent = 'No templates match. Try another category or clear the search.';

    box.appendChild(head); box.appendChild(tools); box.appendChild(oCats); box.appendChild(oStamps);
    box.appendChild(oGrid); box.appendChild(oEmpty);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeOverlay(); });
  }
  function paintOverlayChips() {
    if (!oCats || !oStamps) return;
    var seen = {}, list = [];
    for (var i = 0; i < all.length; i++) {
      for (var k = 0; k < all[i].tags.length; k++) {
        var t = all[i].tags[k];
        if (seen[t]) { seen[t]++; continue; }
        seen[t] = 1; list.push(t);
      }
    }
    list.sort(function (a, b) { return (seen[b] - seen[a]) || a.localeCompare(b); });
    if (oTag && !seen[oTag]) oTag = '';
    oCats.textContent = '';
    oCats.hidden = !list.length;
    if (list.length) {
      chip(oCats, 'data-c4-otag', '', 'All', oTag);
      for (var j = 0; j < list.length; j++) chip(oCats, 'data-c4-otag', list[j], list[j].charAt(0).toUpperCase() + list[j].slice(1), oTag);
    }
    var stamps = stampValues();
    if (oStamp && stamps.indexOf(oStamp) === -1) oStamp = '';
    oStamps.textContent = '';
    oStamps.hidden = stamps.length < 2;
    if (stamps.length >= 2) {
      chip(oStamps, 'data-c4-ostamp', '', 'Any stamps', oStamp);
      for (var s = 0; s < stamps.length; s++) chip(oStamps, 'data-c4-ostamp', stamps[s], stamps[s] + ' stamps', oStamp);
    }
    if (oFilterBtn) {
      var active = (oTag ? 1 : 0) + (oStamp ? 1 : 0);
      oFilterBtn.hidden = !list.length && stamps.length < 2;
      oFilterBtn.textContent = 'Filters' + (active ? ' (' + active + ')' : '');
      oFilterBtn.classList.toggle('is-active', active > 0);
    }
    function chip(hostEl, attr, value, label, current) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'c4-ppc__cat' + (value === current ? ' is-on' : '');
      b.setAttribute(attr, value);
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', value === current ? 'true' : 'false');
      b.textContent = label;
      hostEl.appendChild(b);
    }
  }
  function paintOverlay() {
    if (!oGrid) return;
    var designs = overlayVisible();
    var current = sourceDesignId();
    oGrid.textContent = '';
    if (oCount) oCount.textContent = designs.length + ' of ' + all.length;
    if (oEmpty) oEmpty.hidden = !!designs.length;
    for (var i = 0; i < designs.length; i++) {
      var d = designs[i];
      var item = document.createElement('div');
      item.className = 'c4-ppco__item' + (current && current === d.id ? ' is-selected' : '');
      item.setAttribute('role', 'listitem');
      item.setAttribute('data-design-id', d.id);
      item.setAttribute('data-design-name', d.name);
      if (d.thumbs[0]) item.setAttribute('data-front', d.thumbs[0]);
      if (d.thumbs[1]) item.setAttribute('data-back', d.thumbs[1]);
      var wrap = document.createElement('div');
      wrap.className = 'c4-ppco__imgwrap';
      var img = document.createElement('img');
      img.className = 'c4-ppco__img';
      if (d.thumbs[0]) img.src = d.thumbs[0];
      img.alt = d.name;
      img.loading = 'lazy';
      img.decoding = 'async';
      var go = document.createElement('button');
      go.type = 'button';
      go.className = 'c4-ppco__go';
      go.textContent = 'Customise';
      wrap.appendChild(img); wrap.appendChild(go);
      var name = document.createElement('span');
      name.className = 'c4-ppco__name';
      name.textContent = d.name;
      item.appendChild(wrap); item.appendChild(name);
      oGrid.appendChild(item);
    }
  }
  function openOverlay() {
    buildOverlay();
    oTag = tag;                       // start from the chip the rail is on
    oStamp = '';
    oQuery = '';
    if (oSearch) oSearch.value = '';
    if (oBox) oBox.classList.remove('is-filters-open');
    if (oFilterBtn) oFilterBtn.setAttribute('aria-expanded', 'false');
    paintOverlayChips();
    paintOverlay();
    overlay.hidden = false;
    document.documentElement.classList.add('c4-ppco-open');
    document.addEventListener('keydown', onOverlayKey);
    try { oSearch.focus({ preventScroll: true }); } catch (e) {}
  }
  function closeOverlay() {
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    document.documentElement.classList.remove('c4-ppco-open');
    document.removeEventListener('keydown', onOverlayKey);
    try { rail.focus({ preventScroll: true }); } catch (e) {}
  }
  function onOverlayKey(e) { if (e.key === 'Escape') closeOverlay(); }

  /* ------------------------------------------------------------------ styling ------------- */
  /* Rail styles live in c4-pdp.css (.c4-ppc*). The category chips were only ever styled inside
     the design-tool section, so they come along here. */
  function injectCss() {
    if (document.getElementById('c4-design-carousel-css')) return;
    var s = document.createElement('style');
    s.id = 'c4-design-carousel-css';
    s.textContent =
      '.c4-ppc__cats{display:flex;flex-wrap:wrap;gap:6px;margin:4px 0 2px}' +
      '.c4-ppc__cat{border:1px solid var(--c4-line,#dcdcd9);border-radius:999px;background:#fff;padding:7px 12px;font:600 12px/1 system-ui,sans-serif;color:var(--c4-ink,#101112);cursor:pointer;transition:border-color .12s ease,background-color .12s ease}' +
      '.c4-ppc__cat:hover{border-color:var(--c4-teal,#04a7a7)}' +
      '.c4-ppc__cat.is-on{background:var(--c4-ink,#101112);border-color:var(--c4-ink,#101112);color:#fff}' +
      '.c4-ppc__img{aspect-ratio:90/55}' +
      '.c4-ppcm{position:fixed;inset:0;z-index:9998;background:rgba(16,17,18,.42);display:flex;align-items:center;justify-content:center;padding:20px}' +
      '.c4-ppcm__box{background:#fff;color:var(--c4-ink,#101112);border-radius:16px;padding:22px 22px 18px;width:min(520px,100%);box-shadow:0 20px 60px rgba(0,0,0,.25);font-family:system-ui,-apple-system,"Segoe UI",sans-serif}' +
      '.c4-ppcm__title{margin:0 0 8px;font:800 18px/1.25 system-ui,sans-serif;letter-spacing:-.01em}' +
      '.c4-ppcm__body{margin:0 0 16px;font:400 14px/1.5 system-ui,sans-serif;color:#3d4040}' +
      '.c4-ppcm__row{display:flex;flex-direction:column;gap:8px}' +
      '.c4-ppcm__btn{min-height:44px;border-radius:10px;border:1px solid var(--c4-line,#dcdcd9);background:#fff;color:var(--c4-ink,#101112);font:700 14px/1 system-ui,sans-serif;cursor:pointer;padding:0 16px;text-align:center}' +
      '.c4-ppcm__btn:hover{border-color:var(--c4-teal,#04a7a7)}' +
      '.c4-ppcm__btn--go{background:var(--c4-ink,#101112);border-color:var(--c4-ink,#101112);color:#fff}' +
      '.c4-ppcm__btn--go:hover{background:#000}' +
      '.c4-ppcm__btn--quiet{border-color:transparent;color:#5f6363}' +
      '@media (min-width:560px){.c4-ppcm__row{flex-direction:row;flex-wrap:wrap}.c4-ppcm__btn{flex:1 1 auto}}' +
      /* "See all" tile in the rail */
      '.c4-ppc__all{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;width:100%;height:100%;aspect-ratio:90/55;border:2px dashed var(--c4-line,#dcdcd9);border-radius:8px;background:#fafaf9;color:var(--c4-ink,#101112);cursor:pointer;font-family:system-ui,sans-serif;transition:border-color .12s ease,background-color .12s ease}' +
      '.c4-ppc__all:hover{border-color:var(--c4-teal,#04a7a7);background:#f2fbfb}' +
      '.c4-ppc__alln{font:800 22px/1 system-ui,sans-serif;letter-spacing:-.02em}' +
      '.c4-ppc__allt{font:600 11px/1.2 system-ui,sans-serif;color:#3d4040}' +
      '.c4-ppc__more{margin:6px 0 0;font:600 13px/1.3 system-ui,sans-serif}' +
      '.c4-ppc__morelink{border:0;background:none;padding:0;color:var(--c4-teal,#04a7a7);font:inherit;cursor:pointer;text-decoration:underline;text-underline-offset:3px}' +
      '.c4-ppc__morelink:hover{color:var(--c4-ink,#101112)}' +
      /* the overlay */
      '.c4-ppco-open{overflow:hidden}' +
      '.c4-ppco{position:fixed;inset:0;z-index:9997;background:rgba(16,17,18,.5);display:flex;align-items:center;justify-content:center;padding:16px}' +
      '.c4-ppco[hidden]{display:none}' +
      '.c4-ppco__box{background:#fff;color:var(--c4-ink,#101112);border-radius:16px;width:min(1100px,100%);max-height:min(92vh,900px);display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.3);font-family:system-ui,-apple-system,"Segoe UI",sans-serif;overflow:hidden}' +
      '.c4-ppco__hd{display:flex;align-items:center;gap:10px;padding:18px 20px 10px}' +
      '.c4-ppco__title{margin:0;font:800 20px/1.2 system-ui,sans-serif;letter-spacing:-.01em}' +
      '.c4-ppco__count{font:600 13px/1 system-ui,sans-serif;color:#5f6363;margin-right:auto}' +
      '.c4-ppco__close{border:0;background:transparent;font:400 30px/1 system-ui,sans-serif;color:#5f6363;cursor:pointer;width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center}' +
      '.c4-ppco__close:hover{background:#f2f2f0;color:var(--c4-ink,#101112)}' +
      '.c4-ppco__tools{padding:0 20px 8px;display:flex;gap:8px}' +
      '.c4-ppco__ftoggle{display:none;flex:0 0 auto;min-height:42px;border:1px solid var(--c4-line,#dcdcd9);border-radius:10px;background:#fff;color:var(--c4-ink,#101112);font:700 13px/1 system-ui,sans-serif;padding:0 14px;cursor:pointer;white-space:nowrap}' +
      '.c4-ppco__ftoggle.is-active,.c4-ppco__ftoggle[aria-expanded="true"]{background:var(--c4-ink,#101112);border-color:var(--c4-ink,#101112);color:#fff}' +
      '.c4-ppco__search{width:100%;min-height:42px;border:1px solid var(--c4-line,#dcdcd9);border-radius:10px;padding:0 14px;font:400 14px/1 system-ui,sans-serif;color:var(--c4-ink,#101112);background:#fff}' +
      '.c4-ppco__search:focus{outline:2px solid var(--c4-teal,#04a7a7);outline-offset:0;border-color:var(--c4-teal,#04a7a7)}' +
      '.c4-ppco__cats{padding:0 20px;margin:0 0 6px;max-height:112px;overflow-y:auto}' +
      '.c4-ppc__cat--more{border-style:dashed;color:var(--c4-teal,#04a7a7)}' +
      '.c4-ppco__stamps{margin-bottom:10px}' +
      '.c4-ppco__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:14px;padding:6px 20px 20px;overflow:auto;-webkit-overflow-scrolling:touch}' +
      '.c4-ppco__item{display:flex;flex-direction:column;gap:6px;min-width:0}' +
      '.c4-ppco__imgwrap{position:relative;border:1px solid var(--c4-line,#dcdcd9);border-radius:8px;overflow:hidden;background:#f6f6f4;aspect-ratio:90/55;cursor:pointer;transition:border-color .12s ease,box-shadow .12s ease}' +
      '.c4-ppco__item:hover .c4-ppco__imgwrap,.c4-ppco__item.is-selected .c4-ppco__imgwrap{border-color:var(--c4-teal,#04a7a7);box-shadow:0 0 0 2px rgba(4,167,167,.25)}' +
      '.c4-ppco__img{display:block;width:100%;height:100%;object-fit:contain}' +
      '.c4-ppco__go{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);margin:0;width:auto;white-space:nowrap;border:0;border-radius:999px;background:var(--c4-teal,#04a7a7);color:#fff;font:700 12px/1 system-ui,sans-serif;padding:10px 16px;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,.18);opacity:0;transition:opacity .12s ease}' +
      '.c4-ppco__go:hover{background:#038f8f}' +
      '.c4-ppco__item:hover .c4-ppco__go,.c4-ppco__item:focus-within .c4-ppco__go{opacity:1}' +
      '.c4-ppco__name{font:600 12px/1.3 system-ui,sans-serif;color:var(--c4-ink,#101112);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
      '.c4-ppco__empty{padding:0 20px 20px}' +
      '@media (max-width:639px){.c4-ppco__ftoggle:not([hidden]){display:inline-flex;align-items:center}.c4-ppco__cats{display:none}.c4-ppco__box.is-filters-open .c4-ppco__cats:not([hidden]){display:flex}.c4-ppco{padding:0}.c4-ppco__box{border-radius:0;max-height:100vh;height:100%;width:100%}.c4-ppco__grid{grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;padding:6px 14px 20px}.c4-ppco__hd,.c4-ppco__tools,.c4-ppco__cats{padding-left:14px;padding-right:14px}.c4-ppco__go{opacity:1}}';
    document.head.appendChild(s);
  }

  /* ------------------------------------------------------------------ wiring -------------- */
  function wire() {
    injectCss();
    buildCarousel();
    paintCats();
    paintCarousel();
    resumePending();

    /* The radio group: show/hide with the design route. */
    host.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'radio') syncVisibility();
    });
    /* "Browse our templates" on the card: bring the rail into view instead of opening the editor
       blind. Capture phase, so the card's own handler (which launches) never sees it. */
    document.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-c4-design-browse]') : null;
      if (!b || !card.contains(b) || !all.length) return;
      e.preventDefault();
      e.stopPropagation();
      chooseDesignRoute();
      syncVisibility();
      if (carousel) carousel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, true);
    /* Print.App may refresh its list (e.g. after a save); repaint if it changes length. */
    var lastLen = all.length;
    setInterval(function () {
      var fresh = loadDesigns();
      if (fresh.length !== lastLen) {
        lastLen = fresh.length;
        all = fresh;
        paintCats();
        paintCarousel();
      }
    }, 3000);
  }

  /* Wait for Print.App's client (it mounts after its own loader runs). Up to ~30s, then give up
     quietly -- the card's Customise still opens the editor's own picker. */
  var tries = 0;
  (function boot() {
    all = loadDesigns();
    if (all.length) { wire(); return; }
    if (tries++ > 120) return;
    setTimeout(boot, 250);
  })();
})();
